import { FinancialProductStrategy } from '@generated/graphql';
import type { AssetClassBranch } from '@interfaces/financialProduct';

import { Badge } from '@ovComponents/2-component/badge/badge';

import { AssetClassSelect } from '@ovComponents/3-pattern/assetClassSelect/assetClassSelect';
import { NumberField } from '@ovComponents/3-pattern/numberField/numberField';
import {
  DEFAULT_MODEL_PORTFOLIO_GROUP_BY,
  MODEL_PORTFOLIO_GROUP_BY,
  type ModelPortfolioGroupBy,
} from '@ovComponents/4-module/workflowCompletion/subSteps/createModelPortfolio/modelPortfolioGroupBy';
import { AnalyticsContext } from '@providers/analyticsProvider';
import { ovAnalyticsEvents } from '@utils/analytics/analytics';
/**
 * Only a weight and the asset class it belongs to are read off each holding, so
 * any branch-shaped rows work here — a model's securities or the narrower
 * children a household model portfolio is fetched with.
 */ import { Box, Grid, IconButton, Typography } from 'hephaestus/web';
import { RemoveCircleOutlined as RemoveCircleOutlineIcon } from 'hephaestus/webIcons';
import { mapValues, round } from 'lodash-es';
import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { AssetClassTargetAllocation } from '../assetClassTargetAllocation.types';
export type AllocationWeightSource = {
  percentage?: number;
  financialProduct?: {
    primaryAssetClass?: { id?: string | null };
    secondaryAssetClass?: { id?: string | null };
  };
};

export const AssetClassesTargetAllocations = ({
  targetAllocations,
  setTargetAllocations,
  isFreeForm = false,
  readOnly = false,
  compact = false,
  securities = [],
  testId = 'asset-classes-target-allocations',
  isEditPage = false,
  modelId,
  groupBy = DEFAULT_MODEL_PORTFOLIO_GROUP_BY,
  strategy,
  assetClassBranches,
}: {
  targetAllocations: AssetClassTargetAllocation[];
  setTargetAllocations: (
    targetAllocations: AssetClassTargetAllocation[],
  ) => void;
  isFreeForm?: boolean /**
   * Shows the bands without any way to change them, for recapping a model
   * beside the one being proposed. The actual weights stay, since how they sit
   * against each band is the reason the recap is worth reading.
   */;
  readOnly?: boolean /**
   * Tightens the row so it still fits when two models are shown side by side
   * instead of one across the full step.
   */;
  compact?: boolean;
  securities?: AllocationWeightSource[];
  testId?: string;
  isEditPage?: boolean;
  modelId?: string;
  groupBy?: ModelPortfolioGroupBy;
  strategy?: FinancialProductStrategy;
  assetClassBranches?: AssetClassBranch[];
}) => {
  const { t } = useTranslation(['modelPortfolios', 'buildModels']);
  const { sendAnalytic } = useContext(AnalyticsContext);
  const actualPercentages = useMemo(() => {
    if (strategy === FinancialProductStrategy.AssetClass) {
      const fromBranches: Record<string, number> = {};
      for (const branch of assetClassBranches ?? []) {
        const id = branch.assetClass?.id;
        const tp = branch.targetPercentage;

        if (id != null && tp != null) {
          fromBranches[id] = round(tp, 2);
        }
      }

      return fromBranches;
    }

    const allAllocations = securities.reduce(
      (acc: { [key: string]: number }, security: AllocationWeightSource) => {
        const financialProduct = security.financialProduct;

        const assetClassId =
          groupBy === MODEL_PORTFOLIO_GROUP_BY.SECONDARY
            ? financialProduct?.secondaryAssetClass?.id
            : financialProduct?.primaryAssetClass?.id;

        if (!assetClassId) {
          return acc;
        }

        const percentage = security.percentage || 0;
        acc[assetClassId] = (acc[assetClassId] || 0) + percentage;

        return acc;
      },
      {},
    );

    return mapValues(allAllocations, (x: number) => round(x, 2));
  }, [securities, groupBy, strategy, assetClassBranches]);

  const handleAssetClassChange = (id: string) => (event: any) => {
    const selectedValue = event.target.value;
    // Only update if the selected value is a valid asset class ID
    if (selectedValue && selectedValue !== '') {
      setTargetAllocations(
        targetAllocations.map((allocation) =>
          allocation.secondaryAssetClass.id === id
            ? { ...allocation, secondaryAssetClass: { id: selectedValue } }
            : allocation,
        ),
      );
    }
  };

  const handlePercentageChange =
    (id: string, field: 'min' | 'max' | 'actualPercentage') =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      // Only allow numbers and empty string
      if (
        value === '' ||
        (/^\d*\.?\d*$/.test(value) && Number.parseFloat(value) <= 100)
      ) {
        setTargetAllocations(
          targetAllocations.map((allocation) =>
            allocation.secondaryAssetClass.id === id
              ? {
                  ...allocation,
                  [field]: value === '' ? 0 : Number.parseFloat(value),
                }
              : allocation,
          ),
        );
      }
    };

  const addAllocation = () => {
    const eventName = isEditPage
      ? ovAnalyticsEvents.modelPageEditTabInvestmentPolicyStatementAddAssetClass
      : ovAnalyticsEvents.modelPageCreateStep4AddIpsAddIps;
    sendAnalytic(eventName, {
      ...(isEditPage && modelId ? { modelId } : {}),
      currentAllocationsCount: targetAllocations.length,
      timestamp: new Date().toISOString(),
    });
    const newAllocation: AssetClassTargetAllocation = {
      secondaryAssetClass: {
        id: String(Math.floor(1000000000 + Math.random() * 9000000000)),
      },
      min: 0,
      max: 0,
      actualPercentage: 0,
      color: 'supportOne',
      fromSecurity: false,
    };
    setTargetAllocations([...targetAllocations, newAllocation]);
  };

  const removeAllocation = (id: string) => {
    setTargetAllocations(
      targetAllocations.filter(
        (allocation) => allocation.secondaryAssetClass.id !== id,
      ),
    );
  };

  const usedAssetClasses = targetAllocations
    .map((allocation) => allocation.secondaryAssetClass.id)
    .filter((id): id is string => Boolean(id));

  return (
    <Box
      sx={{
        border: isFreeForm ? 'none' : '1px solid',
        borderColor: 'divider',
        borderRadius: isFreeForm ? 0 : 1,
        pl: isFreeForm ? 0 : compact ? 1 : 2,
        pr: isFreeForm ? 0 : compact ? 1 : 2,
        pt: isFreeForm ? 0 : compact ? 1 : 2,
        pb: isFreeForm ? 0 : 1,
      }}
    >
      {targetAllocations.map((allocation, index) => {
        const assetClassId = allocation.secondaryAssetClass?.id || '';
        const actualPercentage = actualPercentages[assetClassId] || 0;
        const isInBounds =
          actualPercentage >= allocation.min &&
          actualPercentage <= allocation.max;

        return (
          <Grid
            container
            spacing={compact ? 1 : 1.5}
            key={allocation.secondaryAssetClass.id}
            sx={{
              mb: compact ? 1 : 2,
              // Grid pulls the row out by its own spacing so the first field
              // lines up with the padding; overriding that is what used to
              // leave the row hanging past the right edge of the box.
              ...(isFreeForm ? { ml: -1 } : {}),
              ...(compact ? { flexWrap: 'nowrap' } : {}),
            }}
          >
            <Grid
              sx={{
                width: 'auto',
                // Asset class names are long enough to be cut short at any
                // fixed width, so the name column takes whatever the row has
                // spare rather than leaving it as a gap at the end of the row.
                flex: 1,
                minWidth: compact ? '96px' : '160px',
              }}
            >
              <AssetClassSelect
                testId={`${testId}-asset-class-select-${index}`}
                value={allocation.secondaryAssetClass.id || ''}
                setAssetClass={handleAssetClassChange(
                  allocation.secondaryAssetClass.id || '',
                )}
                label={t('assetClass')}
                filteredAllocations={usedAssetClasses}
                omitAny={true}
                disabled={readOnly}
                required={!readOnly}
              />
            </Grid>
            <Grid
              sx={{
                width: '100px',
                // Allowed to give way when two models share the width.
                flex: compact ? '0 1 100px' : 'none',
              }}
            >
              <NumberField
                testId={`${testId}-min-input-${index}`}
                label={t('min')}
                number={allocation.min.toString()}
                setNumber={(value) =>
                  handlePercentageChange(
                    allocation.secondaryAssetClass.id || '',
                    'min',
                  )({
                    target: { value },
                  } as React.ChangeEvent<HTMLInputElement>)
                }
                disabled={readOnly}
                required={!readOnly}
                error={
                  !readOnly && (allocation.min < 0 || allocation.min > 100)
                }
                trailingIcon='percent'
                unsigned
              />
            </Grid>
            <Grid
              sx={{
                width: '100px',
                // Allowed to give way when two models share the width.
                flex: compact ? '0 1 100px' : 'none',
              }}
            >
              <NumberField
                testId={`${testId}-max-input-${index}`}
                label={t('max')}
                number={allocation.max.toString()}
                setNumber={(value) =>
                  handlePercentageChange(
                    allocation.secondaryAssetClass.id || '',
                    'max',
                  )({
                    target: { value },
                  } as React.ChangeEvent<HTMLInputElement>)
                }
                disabled={readOnly}
                required={!readOnly}
                error={
                  !readOnly &&
                  (allocation.max < 0 ||
                    allocation.max > 100 ||
                    allocation.max < allocation.min)
                }
                trailingIcon='percent'
                unsigned
              />
            </Grid>
            <Grid sx={{ width: '76px', flex: 'none' }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  height: '100%',
                }}
              >
                <Typography
                  data-testid={`${testId}-actual-percentage-${index}`}
                  variant='labelSmall'
                  sx={{ mb: '4px', color: 'text.secondary' }}
                >
                  {t('buildModels:actual')}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mt: '7px',
                  }}
                >
                  <Badge
                    label={`${actualPercentage.toFixed(2)}%`}
                    color={isInBounds ? 'positive' : 'negative'}
                    testId={`${testId}-actual-percentage-badge-${index}`}
                    sx={{
                      '& .MuiBadge-badge': {
                        fontSize: '14px',
                        fontFamily: 'Interphases Pro',
                        fontWeight: 400,
                        borderRadius: '4px',
                      },
                    }}
                  />
                </Box>
              </Box>
            </Grid>
            {!readOnly && targetAllocations.length > 1 && (
              <Grid
                sx={{
                  width: '32px',
                  flex: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  mt: 1.5,
                  ml: isFreeForm ? -2 : 0,
                }}
              >
                <IconButton
                  data-testid={`${testId}-remove-allocation-button-${index}`}
                  onClick={() =>
                    removeAllocation(allocation.secondaryAssetClass.id || '')
                  }
                  size='xsmall'
                  sx={{
                    color: 'GrayText',
                    padding: '4px',
                  }}
                  aria-label='delete asset class allocation'
                >
                  <RemoveCircleOutlineIcon fontSize='inherit' />
                </IconButton>
              </Grid>
            )}
          </Grid>
        );
      })}
      {!readOnly && (
        <Typography
          variant='bodySmall'
          component='p'
          data-testid={`${testId}-add-asset-class-branch-button`}
          sx={{
            fontSize: '12px',
            fontWeight: 500,
            cursor: 'pointer',
            '&:hover': {
              opacity: 0.8,
            },
            color: 'primary.main',
            pb: isFreeForm ? 0 : 2,
            display: 'inline-block',
          }}
          onClick={addAllocation}
        >
          {t('addAssetClassBranch')}
        </Typography>
      )}
    </Box>
  );
};
